import { CaretDown, Check } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import React, { ButtonHTMLAttributes, useRef, useState } from 'react';

import { cn } from '../../../../helpers/classBuilders';
import { useOutsideClick } from '../../../../hooks/useOutsideClick';
import { ValueLabelType } from '../../../../types/common';
import { InputField } from '../../../InputField';
import { Paragraph } from '../../../Typography';

// Options may optionally start a new visual group. When `dividerBefore` is set,
// a separator (and an optional `dividerLabel` caption) is rendered above the
// option to set it apart from the ones before it
export type DropdownMultipleOption = ValueLabelType & {
  dividerBefore?: boolean;
  dividerLabel?: string;
  disabled?: boolean;
};

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  label?: string;
  options: DropdownMultipleOption[];
  id: string;
  placeholder?: string;
  value: ValueLabelType[];
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  withSearch?: boolean;
  // When true, the options list closes after each selection change. Defaults to
  // false, keeping the list open so multiple options can be toggled in a row.
  closeOnSelect?: boolean;
  // new
  name: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  onBlur: (e: React.FocusEvent<any>) => void;
  onOpenChange?: (open: boolean) => void;
}

export const DropdownMultiple = ({
  label,
  options,
  id,
  required,
  value,
  placeholder,
  error,
  errorMessage,
  name,
  onChange,
  onBlur,
  onOpenChange,
  disabled,
  withSearch = true,
  closeOnSelect = false,
  ...rest
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const setOpen = (next: boolean) => {
    setIsOpen(next);
    if (!next) {
      setSearchTerm('');
    }
    onOpenChange?.(next);
  };

  // This function will notify Formik of the new selected values
  const handleOptionClick = (option: DropdownMultipleOption) => {
    if (option.disabled) {
      return;
    }
    const optionValue = option.value;
    let newValue: ValueLabelType[];

    const isSelected = value.some(v => v.value === optionValue);

    if (isSelected) {
      newValue = value.filter(v => v.value !== optionValue);
    } else {
      newValue = [...value, option];
    }

    // Simulate a ChangeEvent for Formik/consumers
    const event = {
      target: {
        name,
        value: newValue,
      },
    } as unknown as React.ChangeEvent<any>;

    onChange(event);

    if (closeOnSelect) {
      setOpen(false);
    }
  };

  // This function will handle blur to notify Formik when the dropdown loses focus
  const handleBlur = () => {
    // Simulate a FocusEvent for Formik
    const event = {
      target: {
        name,
      },
    } as React.FocusEvent<any>;
    onBlur(event);
  };

  const renderSelectedOptions = () => {
    if (!value || value.length === 0) {
      return placeholder || 'Select options';
    }

    // Show labels from the selected objects
    const selectedLabels = value.map(v => v.label || v.value);
    return selectedLabels.join(', ');
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current?.contains(event.target as Node)) {
      return;
    }

    setOpen(false);
  };

  /**
   * Handles keydown events to close the popover when the Escape key is pressed.
   */
  useOutsideClick(contentRef, handleClickOutside);

  const renderContent = () => {
    const lowerSearch = searchTerm.toLowerCase();
    const filteredOptions = options?.filter(
      option =>
        option.label.toLowerCase().includes(lowerSearch) ||
        option.value.toLowerCase().includes(lowerSearch),
    );

    if (filteredOptions?.length > 0) {
      return filteredOptions.map(option => {
        const isSelected = value.some(v => v.value === option.value);

        return (
          <React.Fragment key={option.value}>
            {option.dividerBefore && (
              <div className="mt-1 pt-1 border-t border-gray-200">
                {option.dividerLabel && (
                  <p className="px-4 py-1 text-xs tracking-wide text-gray-400">
                    {option.dividerLabel}
                  </p>
                )}
              </div>
            )}
            <div
              className={cn(
                'py-2 px-4 w-full text-sm text-gray-800 rounded-lg focus:outline-hidden',
                option.disabled
                  ? 'cursor-default opacity-60'
                  : 'cursor-pointer hover:bg-gray-100 focus:bg-gray-100',
                {
                  'bg-gray-100': isSelected && !option.disabled,
                },
              )}
              role="option"
              aria-selected={isSelected}
              aria-disabled={option.disabled}
              onClick={() => handleOptionClick(option)}
            >
              <Paragraph tag="span" size="sm" isBolder={isSelected}>
                {option.label}
              </Paragraph>
              {isSelected && (
                <span className="float-end text-blue-700">
                  <Check size={18} />
                </span>
              )}
            </div>
          </React.Fragment>
        );
      });
    }

    return (
      <div className="py-2 px-4 text-sm text-gray-500 text-center">
        No results found
      </div>
    );
  };

  return (
    <div id={`form-field-${id}`} className="relative">
      <div className="w-full text-left">
        {label && (
          <InputField.Label
            id="dropdown"
            htmlFor="dropdown"
            required={required}
          >
            {label}
          </InputField.Label>
        )}
        <div className="hs-select relative">
          <button
            ref={dropdownRef}
            type="button"
            className={cn(
              'hs-select-disabled:pointer-events-none hs-select-disabled:opacity-50 relative py-0 ps-4 pe-9 flex gap-x-2 text-nowrap w-full cursor-pointer bg-white border border-gray-300 rounded-lg text-start focus:outline-hidden focus:border-gray-300 focus-visible:focus-ring-form h-10 items-center disabled:opacity-50 disabled:cursor-default disabled:bg-(--color-input-background-disabled)',
              {
                // The focus: twin matters here too — mousedown focuses the
                // trigger before click opens it, and the base
                // focus:border-gray-300 outranks a plain class while focused.
                'border-(--color-input-border-error) focus:border-(--color-input-border-error)':
                  error && !isOpen,
                // Both variants, deliberately: the base carries
                // `focus:border-gray-300`, and an open trigger is virtually
                // always focused (click opens, blur closes) — without the
                // focus: form that rule outranks the plain class and the
                // highlight never shows. tailwind-merge drops the gray
                // focus rule when this one is present (same variant group).
                // No !error here: while open, the active state outranks error.
                'border-blue-700 focus:border-blue-700': isOpen,
              },
            )}
            aria-expanded={isOpen ? 'true' : 'false'}
            id={id}
            name={name}
            tabIndex={0}
            onBlur={handleBlur}
            onClick={() => {
              setOpen(!isOpen);
            }}
            disabled={disabled}
            aria-disabled={disabled}
            {...rest}
          >
            <span
              className={cn('truncate text-base text-(--color-font-primary)', {
                'text-gray-500': !value || value.length === 0,
              })}
            >
              {renderSelectedOptions()}
            </span>
          </button>

          <div
            className={cn('absolute top-1/2 end-3 -translate-y-1/2', {
              'text-gray-400': disabled,
            })}
          >
            <CaretDown size={18} aria-hidden="true" />
          </div>
        </div>

        <InputField.ErrorMessage
          id={id}
          error={error}
          errorMessage={errorMessage}
        />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1, transition: { duration: 0.1 } }}
            exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
            ref={contentRef}
            id={id}
            className="absolute mt-2 max-h-72 pb-1 px-1 space-y-0.5 z-20 w-full bg-white border border-gray-300 rounded-lg overflow-hidden overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 top-full opened"
            role="listbox"
            aria-orientation="vertical"
            aria-labelledby={`${id}-header`}
            aria-describedby={`${id}-body`}
            tabIndex={-1}
          >
            {withSearch ? (
              <div className="bg-white p-2 -mx-1 sticky top-0">
                <InputField
                  id="dropdown-search"
                  type="search"
                  placeholder="Search..."
                  name="search"
                  onChange={e => setSearchTerm(e.target.value)}
                  value={searchTerm}
                  onClear={() => setSearchTerm('')}
                  autoFocus
                />
              </div>
            ) : null}

            {renderContent()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
